"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { DistinctionMark } from "@/components/guide-public/guide-ornaments";
import { GuidePhoto } from "@/components/guide-public/guide-parts";
import { GuideSaveButton, useSavedPlaces } from "@/components/guide-public/guide-saved";
import { sharedListQuery } from "@/lib/virtualspace/guide/guide-lists";
import { countCopy } from "@/lib/virtualspace/guide/guide-public-copy";

/** A place as the saved page shows it: what /api/localized/places returns. */
export type SavedPlaceCard = {
  slug: string;
  name: string;
  twin: string | null;
  category: string;
  region: string;
  distinction: number;
  distinctionName: string;
  bestFor: string | null;
  coverUrl: string | null;
  href: string;
  latitude: number | null;
  longitude: number | null;
};

type Copy = {
  title: string;
  empty: string;
  note: string;
  countOne: string;
  countMany: string;
  listName: string;
  listPlaceholder: string;
  share: string;
  copyLink: string;
  copied: string;
  clear: string;
  confirmClear: string;
  save: string;
  saved: string;
  unsave: string;
  map: string;
};

/**
 * The visitor's saved places (this device), as cards, with a name box that
 * turns them into a shareable list: the link carries the name and the
 * places, so it needs no account and opens for anyone.
 */
export function GuideSavedView({ root, lang, copy, mapHref }: { root: string; lang: "en" | "ar"; copy: Copy; mapHref: string }) {
  const { saved, ready, clear } = useSavedPlaces();
  const [cards, setCards] = useState<SavedPlaceCard[]>([]);
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);
  const key = saved.join(",");

  useEffect(() => {
    if (!ready) return;
    if (saved.length === 0) {
      setCards([]);
      return;
    }
    const controller = new AbortController();
    fetch(`/api/localized/places?slugs=${encodeURIComponent(key)}&lang=${lang}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : { places: [] }))
      .then((data: { places: SavedPlaceCard[] }) => setCards(data.places ?? []))
      .catch(() => {});
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready, lang]);

  const ordered = useMemo(() => {
    const bySlug = new Map(cards.map((c) => [c.slug, c]));
    return saved.flatMap((s) => (bySlug.get(s) ? [bySlug.get(s) as SavedPlaceCard] : []));
  }, [cards, saved]);

  const shareHref = `${root}/lists?${sharedListQuery({ name, slugs: saved })}${lang === "ar" ? "&lang=ar" : ""}`;
  const share = async () => {
    const url = new URL(shareHref, window.location.origin).toString();
    try {
      if (navigator.share) {
        await navigator.share({ url, title: name || copy.title });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The visitor closed the sheet.
    }
  };

  if (!ready) return <p style={{ margin: 0, color: "var(--eg-muted)" }}>…</p>;
  if (saved.length === 0) {
    return (
      <p className="eg-panel" style={{ margin: 0, padding: 20, fontSize: 17 }}>
        {copy.empty}{" "}
        <Link href={mapHref} style={{ fontWeight: 600 }}>
          {copy.map} →
        </Link>
      </p>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div className="eg-panel" style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, flex: "1 1 260px" }}>
          <span className="eg-eyebrow">{copy.listName}</span>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 80))} placeholder={copy.listPlaceholder} className="eg-input" />
        </label>
        <button type="button" className="eg-button eg-button--primary" onClick={share} data-eg-click="share" data-eg-subject-type="page" data-eg-subject="saved">
          {copied ? copy.copied : copy.share}
        </button>
        <a href={shareHref} className="eg-button eg-button--ghost">
          {copy.copyLink}
        </a>
        <span style={{ flex: "1 1 auto" }} />
        <span style={{ color: "var(--eg-muted)" }}>{countCopy(copy.countOne, copy.countMany, saved.length)}</span>
        <button
          type="button"
          className="eg-mini-button"
          onClick={() => {
            if (window.confirm(copy.confirmClear)) clear();
          }}
        >
          {copy.clear}
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 14, color: "var(--eg-muted)" }}>{copy.note}</p>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 360px), 1fr))", gap: 16 }}>
        {ordered.map((c) => (
          <li key={c.slug} style={{ display: "flex", gap: 16, alignItems: "center", background: "var(--eg-card)", borderRadius: 4, padding: 12 }}>
            <Link href={c.href} style={{ flex: "none" }}>
              <GuidePhoto src={c.coverUrl} alt="" label="" sizes="140px" style={{ width: 140, height: 104, objectFit: "cover", borderRadius: 2, display: "block" }} />
            </Link>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
              <Link href={c.href} className="eg-display" style={{ fontSize: 22, color: "var(--eg-ink)", textDecoration: "none", lineHeight: 1.2 }}>
                {c.name}
              </Link>
              <span style={{ fontSize: 14, color: "var(--eg-muted)", display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <DistinctionMark count={c.distinction} size={12} />
                {c.category} · {c.region}
              </span>
              {c.bestFor ? <span style={{ fontSize: 14 }}>{c.bestFor}</span> : null}
              <GuideSaveButton slug={c.slug} copy={{ save: copy.save, saved: copy.saved, unsave: copy.unsave }} size="sm" style={{ alignSelf: "flex-start" }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Save all to my places" on a shared list. */
export function GuideSaveAllButton({ slugs, label, doneLabel }: { slugs: string[]; label: string; doneLabel: string }) {
  const { saved, add } = useSavedPlaces();
  const allIn = slugs.every((s) => saved.includes(s));
  return (
    <button type="button" className="eg-button eg-button--primary" disabled={allIn} onClick={() => add(slugs)} data-eg-click="save" data-eg-subject-type="page" data-eg-subject="list">
      {allIn ? doneLabel : label}
    </button>
  );
}
